export function calculateActualPpcm(
  baseLength: number,
  newLength: number,
  basePpcm: number
): number {
  const sizeDiff = newLength / baseLength
  return basePpcm * sizeDiff
}

export function calculateSize(length: number, ppcm: number): number {
  return Math.round((length / ppcm) * 10)
}

/**
 * Checks if logo dimensions fit within the transfer method maximum size.
 * Considers both normal and rotated orientations.
 * @param width Logo width in mm
 * @param height Logo height in mm
 * @param maxSize Maximum size string in format "WIDTHxHEIGHT" (e.g., "320x380")
 * @returns true if the logo fits within the maximum dimensions
 */
export function doesLogoFitInTransferMethod(
  width: number,
  height: number,
  maxSize: string
): boolean {
  if (!maxSize || !maxSize.includes('x')) {
    return true; // If no size restriction, allow any size
  }

  const [maxWidth, maxHeight] = maxSize.split('x').map(Number);
  
  // Check if dimensions are valid
  if (isNaN(maxWidth) || isNaN(maxHeight) || maxWidth <= 0 || maxHeight <= 0) {
    return true; // If invalid max size, allow any size
  }

  // Check normal orientation
  const fitsNormal = width <= maxWidth && height <= maxHeight;
  
  // Check rotated orientation (90 degrees)
  const fitsRotated = width <= maxHeight && height <= maxWidth;
  
  return fitsNormal || fitsRotated;
}

/**
 * Corrects logo dimensions to fit within the transfer method maximum size.
 * Maintains aspect ratio and considers both orientations.
 * @param width Current logo width in mm
 * @param height Current logo height in mm
 * @param maxSize Maximum size string in format "WIDTHxHEIGHT" (e.g., "320x380")
 * @returns Corrected dimensions {width, height}
 */
export function correctLogoSizeForTransferMethod(
  width: number,
  height: number,
  maxSize: string
): { width: number; height: number } {
  if (!maxSize || !maxSize.includes('x')) {
    return { width, height }; // If no size restriction, return original
  }

  const [maxWidth, maxHeight] = maxSize.split('x').map(Number);
  
  // Check if dimensions are valid
  if (isNaN(maxWidth) || isNaN(maxHeight) || maxWidth <= 0 || maxHeight <= 0) {
    return { width, height }; // If invalid max size, return original
  }

  // If already fits, return original dimensions
  if (doesLogoFitInTransferMethod(width, height, maxSize)) {
    return { width, height };
  }

  const aspectRatio = width / height;

  // Calculate scale factors for both orientations
  const scaleNormal = Math.min(maxWidth / width, maxHeight / height);
  const scaleRotated = Math.min(maxHeight / width, maxWidth / height);

  // Choose the orientation that allows for the largest size
  if (scaleNormal >= scaleRotated) {
    // Use normal orientation
    return {
      width: Math.round(width * scaleNormal * 100) / 100,
      height: Math.round(height * scaleNormal * 100) / 100
    };
  } else {
    // Use rotated orientation - but keep the same aspect ratio
    return {
      width: Math.round(width * scaleRotated * 100) / 100,
      height: Math.round(height * scaleRotated * 100) / 100
    };
  }
}
